import { useTranslation } from "react-i18next";
import { ArrowRight, Eye } from "lucide-react";

import { useEmdrSets } from "../../application/useEmdrSets";
import { summarizeSets } from "../../domain/emdrSummary";
import EmdrRatingCharts from "./EmdrRatingCharts";

type Props = {
    sessionId: string;
    /** Takes the therapist to the session's EMDR tab, with every set. */
    onShowSets: () => void;
};

/**
 * The EMDR work at a glance, on the session's overview: where the target
 * started and ended, and the curves — once there is more than one set, since
 * a single set has no course to show. Every set is one tab away.
 */
export default function EmdrSessionSummary({ sessionId, onShowSets }: Props) {
    const { t } = useTranslation();
    const { sets } = useEmdrSets(sessionId);
    if (sets.length < 2) return null;
    const summary = summarizeSets(sets);

    return (
        <section
            aria-labelledby="emdr-summary-title"
            className="glass-effect glass-border flex flex-col gap-5 rounded-2xl border p-6 shadow-theme-sm"
            data-testid="emdr-session-summary"
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 id="emdr-summary-title" className="flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                    <Eye className="h-5 w-5 text-accent-text" aria-hidden="true" />
                    {t("emdr.session.summaryTitle")}
                </h3>
                <button
                    type="button"
                    onClick={onShowSets}
                    className="inline-flex min-h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-accent-text hover:bg-surface-sunken"
                >
                    {t("emdr.session.showSets")}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>

            <dl className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-surface-sunken px-4 py-3">
                    <dt className="text-xs text-content-tertiary">{t("emdr.session.setsCount")}</dt>
                    <dd className="text-xl font-semibold tabular-nums text-content-primary">{summary.count}</dd>
                </div>
                <div className="rounded-xl bg-surface-sunken px-4 py-3">
                    <dt className="text-xs text-content-tertiary">{t("emdr.session.sudCourse")}</dt>
                    <dd className="text-xl font-semibold tabular-nums text-content-primary">
                        {summary.firstSud === null ? "–" : `${summary.firstSud} → ${summary.lastSud}`}
                    </dd>
                </div>
                <div className="rounded-xl bg-surface-sunken px-4 py-3">
                    <dt className="text-xs text-content-tertiary">{t("emdr.session.lastVoc")}</dt>
                    <dd className="text-xl font-semibold tabular-nums text-content-primary">{summary.lastVoc ?? "–"}</dd>
                </div>
            </dl>

            <EmdrRatingCharts sets={sets} />
        </section>
    );
}
